import React, { useState, useEffect, useRef, useCallback } from 'react';
import { favoritesApi, Favorite, FavoriteConfig } from '../../../api/favoritesApi';
import { Star, Trash, X } from '../Icons';
import { Button } from '../Button';
import './FavoritesDropdown.css';

export interface FavoritesDropdownProps {
  /** Unique key for this page (e.g., 'all_timesheets') */
  pageKey: string;
  /** Get the current config state to save as a favorite */
  getCurrentConfig: () => FavoriteConfig;
  /** Apply a saved favorite config (restore filters, group_by, search) */
  applyConfig: (config: FavoriteConfig) => void;
  /** Called when the active favorite changes (name or null) */
  onActiveFavoriteChange?: (name: string | null) => void;
}

export function FavoritesDropdown({
  pageKey,
  getCurrentConfig,
  applyConfig,
  onActiveFavoriteChange,
}: FavoritesDropdownProps) {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [shareWithAll, setShareWithAll] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDefault, setEditDefault] = useState(false);
  const [editShared, setEditShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [defaultApplied, setDefaultApplied] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  // Load favorites
  const loadFavorites = useCallback(async () => {
    try {
      const data = await favoritesApi.getFavorites(pageKey);
      setFavorites(data);
      return data;
    } catch {
      // silently fail
      return [];
    }
  }, [pageKey]);

  // Load on mount and apply default
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const favs = await loadFavorites();
      if (cancelled) return;
      if (!defaultApplied && favs.length > 0) {
        const defaultFav = favs.find((f) => f.is_default);
        if (defaultFav) {
          applyConfig(defaultFav.config);
          onActiveFavoriteChange?.(defaultFav.name);
        }
        setDefaultApplied(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [pageKey]);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
        setShowSaveForm(false);
        setEditingId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save current search as favorite
  const handleSave = async () => {
    if (!saveName.trim()) return;
    try {
      setLoading(true);
      await favoritesApi.createFavorite({
        page_key: pageKey,
        name: saveName.trim(),
        config: getCurrentConfig(),
        is_default: saveAsDefault,
        shared: shareWithAll,
      });
      setSaveName('');
      setSaveAsDefault(false);
      setShareWithAll(false);
      setShowSaveForm(false);
      await loadFavorites();
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  // Apply a favorite
  const handleApply = (fav: Favorite) => {
    applyConfig(fav.config);
    onActiveFavoriteChange?.(fav.name);
    setShowMenu(false);
  };

  // Delete a favorite
  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await favoritesApi.deleteFavorite(id);
      await loadFavorites();
    } catch {
      // silently fail
    }
  };

  // Start editing
  const handleStartEdit = (fav: Favorite, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(fav.id);
    setEditName(fav.name);
    setEditDefault(fav.is_default);
    setEditShared(fav.shared);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    try {
      setLoading(true);
      await favoritesApi.updateFavorite(editingId, {
        name: editName.trim(),
        is_default: editDefault,
        shared: editShared,
      });
      setEditingId(null);
      await loadFavorites();
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fav-dropdown-container" ref={menuRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setShowMenu(!showMenu);
          setShowSaveForm(false);
          setEditingId(null);
        }}
      >
        <Star />
        Favorites
      </Button>

      {showMenu && (
        <div className="fav-dropdown-menu">
          {/* List of saved favorites */}
          {favorites.length > 0 && (
            <div className="fav-list">
              {favorites.map((fav) => (
                <div key={fav.id} className="fav-item-wrapper">
                  {editingId === fav.id ? (
                    // Edit form
                    <div className="fav-edit-form">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="fav-edit-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                      />
                      <label className="fav-checkbox">
                        <input
                          type="checkbox"
                          checked={editDefault}
                          onChange={(e) => setEditDefault(e.target.checked)}
                        />
                        Use by default
                      </label>
                      <label className="fav-checkbox">
                        <input
                          type="checkbox"
                          checked={editShared}
                          onChange={(e) => setEditShared(e.target.checked)}
                        />
                        Share with all users
                      </label>
                      <div className="fav-edit-actions">
                        <button
                          className="fav-btn fav-btn-save"
                          onClick={handleSaveEdit}
                          disabled={loading || !editName.trim()}
                        >
                          SAVE
                        </button>
                        <button
                          className="fav-btn fav-btn-cancel"
                          onClick={() => setEditingId(null)}
                        >
                          <X />
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Favorite item
                    <button
                      className={`fav-item ${fav.is_default ? 'fav-item-default' : ''}`}
                      onClick={() => handleApply(fav)}
                    >
                      <span className="fav-item-name">
                        {fav.is_default && <span className="fav-default-badge">✓</span>}
                        {fav.name}
                      </span>
                      <span className="fav-item-actions">
                        <span
                          className="fav-item-action"
                          onClick={(e) => handleStartEdit(fav, e)}
                          title="Edit"
                        >
                          ✎
                        </span>
                        <span
                          className="fav-item-action fav-item-delete"
                          onClick={(e) => handleDelete(fav.id, e)}
                          title="Delete"
                        >
                          <Trash />
                        </span>
                      </span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {favorites.length > 0 && <div className="fav-divider" />}

          {/* Save current search */}
          {!showSaveForm ? (
            <button
              className="fav-save-trigger"
              onClick={() => setShowSaveForm(true)}
            >
              Save current search ▸
            </button>
          ) : (
            <div className="fav-save-form">
              <input
                type="text"
                placeholder="Filter name..."
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                className="fav-save-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
              <label className="fav-checkbox">
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                />
                Use by default
              </label>
              <label className="fav-checkbox">
                <input
                  type="checkbox"
                  checked={shareWithAll}
                  onChange={(e) => setShareWithAll(e.target.checked)}
                />
                Share with all users
              </label>
              <button
                className="fav-btn fav-btn-save"
                onClick={handleSave}
                disabled={loading || !saveName.trim()}
              >
                SAVE
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FavoritesDropdown;
